/**
 * Shared helpers for narrowing `unknown` catch values.
 *
 * Every async call site in the frontend used to repeat one of two patterns:
 *   const message = err instanceof Error ? err.message : 'Unknown error';
 *   if (err instanceof Error) throw err; else throw new Error(fallback);
 *
 * Consolidating them here eliminates the cross-pattern duplication flagged by
 * the code review (`err instanceof Error` appeared in App.tsx, useTailNumbers.ts,
 * api.ts, SectionResults.tsx, and auth.ts) and ensures a single place to update
 * the user-visible "Unknown error" copy or the re-throw semantics.
 */

/**
 * Extract a human-readable message from an unknown catch value.
 * Returns the Error's message verbatim, or the provided fallback string when
 * the thrown value is not an Error instance.
 */
export function errorMessage(err: unknown, fallback: string = 'Unknown error'): string {
  return err instanceof Error ? err.message : fallback;
}

/**
 * Normalize an unknown catch value into a typed Error.
 * Existing Error instances are returned as-is so the original stack survives;
 * non-Error values are wrapped in `new Error(fallbackMessage)` so callers can
 * always rely on receiving an Error and never an arbitrary thrown value.
 */
export function toError(err: unknown, fallbackMessage: string): Error {
  return err instanceof Error ? err : new Error(fallbackMessage);
}
